/**
 * formatCellValue — Single source of truth for formatting cell values.
 *
 * Consolidates the inline version from school.tsx and the older formatters.ts
 * into one utility. All lookup maps are referenced here.
 */

import {
  OWNERSHIP_TYPE,
  LOCATION_TYPE,
  SPONSORED_BY,
  STATION_TYPE,
  SCHOOL_BUILDING,
  BUILDING_TYPE,
  INFRA_FACILITY,
  PM_SHRI_PHASE,
  INTERNET_PLAN,
  INTERNET_CONNECTIVITY,
  CONTACT_ROLE,
  INFRA_SIZE_TYPE,
  INTERNET_TYPE,
  NCC_GROUP,
  NCC_GROUP_WING,
  NCC_GROUP_ANO_CTO,
  NCC_ANO_CTO_RANK,
  SPORT_CATEGORY,
  SPORT_TITLE,
  SPORT_QUALITY,
  SPORT_MEASURE_UNIT,
  INFRA_NAME,
  LAB_FUNDED_BY,
} from '../constants/lookupMaps';

// Normalize key for lookup
const normalize = (k?: string) => (k || '').replace(/\s+/g, '').replace(/\./g, '').toLowerCase();

// Lookup table keyed by normalized field name
const LOOKUP_BY_KEY: Record<string, Record<string, string> | undefined> = {
  ownership: OWNERSHIP_TYPE,
  ownershiptype: OWNERSHIP_TYPE,
  locationtype: LOCATION_TYPE,
  location: LOCATION_TYPE,
  sponsoredby: SPONSORED_BY,
  sponsored: SPONSORED_BY,
  stationtype: STATION_TYPE,
  schoolbuilding: SCHOOL_BUILDING,
  buildingtype: BUILDING_TYPE,
  infrafacility: INFRA_FACILITY,
  ispmshree: PM_SHRI_PHASE,
  pmshri: PM_SHRI_PHASE,
  pmshriphase: PM_SHRI_PHASE,
  internetplan: INTERNET_PLAN,
  internetype: INTERNET_TYPE,
  internetconnectivity: INTERNET_CONNECTIVITY,
  contactrole: CONTACT_ROLE,
  role: CONTACT_ROLE,
  infrasizetype: INFRA_SIZE_TYPE,
  sizetype: INFRA_SIZE_TYPE,
  nccgroup: NCC_GROUP,
  nccgroupwing: NCC_GROUP_WING,
  nccgroupanocto: NCC_GROUP_ANO_CTO,
  nccanoctorank: NCC_ANO_CTO_RANK,
  sporttype: SPORT_TITLE,
  sporttitle: SPORT_TITLE,
  sportstitle: SPORT_TITLE,
  sportcategory: SPORT_CATEGORY,
  quality: SPORT_QUALITY,
  measurementunit: SPORT_MEASURE_UNIT,
  name: INFRA_NAME,
  infraname: INFRA_NAME,
  fundedby: LAB_FUNDED_BY,
  funded: LAB_FUNDED_BY,
};

/**
 * Format a raw cell value for display.
 *
 * 1. Applies lookup-map translation when the field name matches a known map.
 * 2. Handles isPMShree (phase numbers).
 * 3. Converts 0/1 → Yes/No for recognized boolean fields.
 * 4. Falls back to String(value).
 */
export const formatCellValue = (value: any, key?: string): string => {
  if (value === null || value === undefined) return '-';

  // 1. Lookup map
  if (key) {
    const nk = normalize(key);
    const map = LOOKUP_BY_KEY[nk];
    if (map) {
      const mapped = (map as any)[String(value)];
      if (mapped !== undefined) return String(mapped);
    }
  }

  // 2. PM Shree phase
  if (key === 'isPMShree') {
    const n = Number(value);
    if (n === 0) return 'No';
    if (n >= 1) return `Phase ${n}`;
    return String(value);
  }

  // 3. Boolean fields
  const isBooleanField = key
    ? /^is[A-Z]/.test(key) ||
      key.toLowerCase().endsWith('status') ||
      key.toLowerCase().endsWith('active') ||
      key.toLowerCase().endsWith('available') ||
      [
        'Internate Available In Library',
        'Printer For Bar Code',
        'Doc Scanner Available',
        'Projector Available',
        'internateAvailableInLibrary',
        'printerForBarCode',
        'docScannerAvailable',
        'projectorAvailable',
        'internate_available_in_library',
        'printer_for_bar_code',
        'doc_scanner_available',
        'projector_available',
        'libraryAutomationSystem',
      ].includes(key)
    : false;

  if (isBooleanField && (value === 0 || value === 1 || value === '0' || value === '1')) {
    return value === 1 || value === '1' ? 'Yes' : 'No';
  }

  return String(value);
};
